import { useState, useEffect } from "react";
import clsx from 'clsx'
import Swal from 'sweetalert2';
import { useIntl } from 'react-intl';
import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import { IClientes } from "../../interfaces/IClientes";
import ClientesService from "../../services/ClientesService";
import ClientesModal from "./ClientesModal";
import ClientesTabela from "./ClientesTabela";
import workflowService from "../workflow-aprovacao/workflowService";
import maestroService from "../maestro-contabil/maestroService";
/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

type WfStatus = { goLiveAtivo: boolean; progressoPercent: number; etapasConcluidas: number };
type McStatus = { status: string; percentualConcluido: number; etapasConcluidas: number; etapasTotais: number };

function WfBadge({ status, carregando }: { status?: WfStatus; carregando: boolean }) {
  if (carregando) {
    return <span className="badge badge-light-secondary">...</span>;
  }
  if (!status || status.etapasConcluidas === 0) {
    return <span className="badge badge-light-secondary">Não iniciado</span>;
  }
  if (status.goLiveAtivo) {
    return <span className="badge badge-light-success">Go-Live Ativo</span>;
  }
  return (
    <span className="badge badge-light-warning">
      {status.etapasConcluidas}/5 etapas
    </span>
  );
}

function McBadge({ status, carregando }: { status?: McStatus; carregando: boolean }) {
  if (carregando) {
    return <span className="badge badge-light-secondary">...</span>;
  }
  if (!status || status.etapasConcluidas === 0) {
    return <span className="badge badge-light-secondary">Não iniciado</span>;
  }
  if (status.status === 'ATIVO') {
    return <span className="badge badge-light-success">Ativo</span>;
  }
  return (
    <span className="badge badge-light-warning">
      {status.etapasConcluidas}/{status.etapasTotais} etapas
    </span>
  );
}

function Clientes() {
  const { t } = useTranslation();
  const intl = useIntl();
  const [clientes, setClientes] = useState<IClientes[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [clienteSelecionado, setClienteSelecionado] = useState<IClientes>({} as IClientes);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [paginaCarregada, setPaginaCarregada] = useState<boolean>(false);
  const [wfStatuses, setWfStatuses] = useState<Record<number, WfStatus>>({});
  const [wfCarregando, setWfCarregando] = useState(false);
  const [mcStatuses, setMcStatuses] = useState<Record<number, McStatus>>({});
  const [mcCarregando, setMcCarregando] = useState(false);
  const { classes } = useLayout();

  const columns = [
    {
      name: t('Nome Fantasia'),
      selector: (row: IClientes) => row['nomeFantasia'],
      sortable: true,
    },
    {
      name: 'CNPJ',
      selector: (row: IClientes) => row['cnpj'],
      sortable: true,
    },
    {
      name: t('Razão Social'),
      selector: (row: IClientes) => row['razaoSocial'],
      sortable: true,
    },
    {
      name: t('Contato'),
      selector: (row: IClientes) => row['telefone'],
      sortable: true,
    },
    {
      name: 'WorkFlow',
      width: '140px',
      cell: (row: IClientes) => (
        <WfBadge status={wfStatuses[row.id]} carregando={wfCarregando} />
      ),
    },
    {
      name: 'Maestro',
      width: '140px',
      cell: (row: IClientes) => (
        <McBadge status={mcStatuses[row.id]} carregando={mcCarregando} />
      ),
    },
    {
      name: t('Ações'),
      cell: (row: IClientes) => (
        <AcoesClientes
          inativarCliente={inativarCliente}
          editarCliente={(cliente: IClientes) => {
            setModalTitle('Editar Cliente');
            setClienteSelecionado(cliente);
            toggleModal();
          }}
          cliente={row}
        />
      ),
    }
  ];

  useEffect(() => {
    setTimeout(() => {
      carregarInformacoes();
    }, 1000)
  }, []);

  async function carregarInformacoes() {
    setPaginaCarregada(false);
    await ClientesService.obterTodos(async (lista) => {
      setClientes(lista);
      await carregarWfStatus(lista);
      await carregarMcStatus(lista);
    });
    setPaginaCarregada(true);
  }

  async function carregarWfStatus(lista: IClientes[]) {
    const ids = lista.map(c => c.id).filter((id): id is number => !!id);
    if (ids.length === 0) return;
    setWfCarregando(true);
    try {
      const statuses = await workflowService.onboardingStatusBatch(ids);
      const map: Record<number, WfStatus> = {};
      statuses.forEach(s => { map[s.tenantId] = s; });
      setWfStatuses(map);
    } catch {
      // status indisponível não bloqueia a tela
    } finally {
      setWfCarregando(false);
    }
  }

  async function carregarMcStatus(lista: IClientes[]) {
    const ids = lista.map(c => c.id).filter((id): id is number => !!id);
    if (ids.length === 0) return;
    setMcCarregando(true);
    try {
      const statuses = await maestroService.obterSetupStatusBatch(ids);
      const map: Record<number, McStatus> = {};
      statuses.forEach(s => { map[s.clienteId] = s; });
      setMcStatuses(map);
    } catch {
      // status indisponível não bloqueia a tela (ex.: usuário sem perfil Master)
    } finally {
      setMcCarregando(false);
    }
  }

  function AcoesClientes({
    cliente,
    inativarCliente,
    editarCliente,
  }: {
    cliente: IClientes;
    inativarCliente: (c: IClientes) => void;
    editarCliente: (c: IClientes) => void;
  }) {
    return (
      <div data-tag="allowRowEvents" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <i
          onClick={() => editarCliente(cliente)}
          className="fas fa-edit fs-1"
          title="Editar cliente — inclui as abas Workflow e Maestro Contábil"
          style={{ color: '#5E6397', cursor: 'pointer' }}
        />
        <i
          onClick={() => inativarCliente(cliente)}
          className="fas fa-trash-alt fs-1"
          style={{ color: '#5E6397', cursor: 'pointer' }}
        />
      </div>
    );
  }

  function toggleModal(recarregarInformacoes?: boolean): void {
    if (recarregarInformacoes) {
      carregarInformacoes();
    }

    setShowModal(!showModal);

    if (showModal) {
      setTimeout(() => {
        setClienteSelecionado({ id: 0 } as IClientes);
      }, 300);
    }
  }

  async function inativarCliente(cliente: IClientes) {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-modal-excluir ms-5',
        cancelButton: 'btn btn-modal-cancelar'
      },
      buttonsStyling: false
    });
    const popup = await swalWithBootstrapButtons.fire({
      title: 'Inativar Cliente',
      html: `Deseja realmente inativar este cliente?<br>
      As empresas e grupos relacionados à esse cliente também ficarão indisponíveis.`,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: `Inativar`,
      reverseButtons: true
    });

    if (popup.isConfirmed) {
      await ClientesService.inativar(cliente.id, () => setClientes(clientes.filter(e => e.id !== cliente.id)), 'Cliente inativado com sucesso');
    }
  }

  return <>
    <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.CLIENTS' })}</PageTitle>
    <div className='toolbar' id='kt_toolbar'>
      <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
        <DefaultTitle />
        <ClientesModal cliente={clienteSelecionado} title={modalTitle} show={showModal} toggleModal={toggleModal} />
        <div className='d-flex align-items-center py-1'>
          <button onClick={() => {
            setModalTitle('Adicionar Cliente');
            setClienteSelecionado({ id: 0 } as IClientes);
            toggleModal();
          }} className='btn btn-sm btn-primario'>
            <i className="icon-lg text-white fas fa-plus"></i>
            <span style={{ verticalAlign: 'middle' }}> CLIENTE </span>
          </button>
        </div>
      </div>
    </div>

    {paginaCarregada ? clientes.length > 0 ? <ClientesTabela colunas={columns} itens={clientes} /> : <h2>Não possuem clientes para serem exibidos</h2> : <p>Carregando...</p>}
  </>
}

export default Clientes;
